export type PreviewKind = 'pdf' | 'docx' | 'image' | null;

const IMAGE_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/svg+xml',
]);

// Only .docx (ZIP+XML) renders client-side via docx-preview. Legacy binary
// .doc (OLE2) is not client-previewable — it previews only if the server
// supplies a converted preview.url (handled by canPreview's preview.url check).
const DOCX_MIME = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const IMAGE_EXT = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'svg',
]);

const DOCX_EXT = new Set(['docx']);

type PreviewableFile = {
  content_type?: string;
  type?: string;
  file_name?: string;
  filename?: string;
  name?: string;
  // The URL the preview would actually load. Optional because most current
  // callers never pass it at all (a different mechanism resolves it) — only
  // an EXPLICIT empty string is treated as "nothing to preview" below, so
  // omitting the field entirely keeps deferring to the content-type/extension
  // heuristic exactly as before.
  url?: string;
  preview?: { url?: string } | null;
};

const getExtension = (file: PreviewableFile): string | null => {
  const name = file.file_name || file.filename || file.name;
  if (!name) return null;
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : null;
};

export const getPreviewKind = (file: PreviewableFile): PreviewKind => {
  if (!file) return null;
  const mime = (file.content_type || file.type || '').toLowerCase();
  if (mime === 'application/pdf') return 'pdf';
  if (IMAGE_MIME.has(mime)) return 'image';
  if (DOCX_MIME.has(mime)) return 'docx';
  const ext = getExtension(file);
  if (!ext) return null;
  if (ext === 'pdf') return 'pdf';
  if (IMAGE_EXT.has(ext)) return 'image';
  if (DOCX_EXT.has(ext)) return 'docx';
  return null;
};

export const canPreview = (file: PreviewableFile): boolean => {
  if (!file) return false;
  // A server-provided preview.url always wins, regardless of the top-level
  // url — this is the legacy-binary-DOC-with-a-converted-preview case.
  if (file.preview?.url) return true;
  // An explicit `url: ''` (with no preview.url either) is the caller telling
  // us there is genuinely nothing to render — e.g. AttachFilesPicker's
  // just-uploaded chip, built from a PUT response that omitted a signed URL.
  // Trust that over the content-type/extension heuristic below; otherwise the
  // 👁 renders and opens a blank preview. Callers that never set `url` at all
  // (the common case, resolved some other way) are unaffected — this only
  // fires on an explicit "no url".
  if (file.url === '') return false;
  return getPreviewKind(file) !== null;
};
